import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { API_BASE_URL } from '../../config';
import './embed.css';

const EmbedEventsTicker = () => {
  const [searchParams] = useSearchParams();

  // URL params for customization
  const theme = searchParams.get('theme') || 'dark';
  const bg = searchParams.get('bg') || (theme === 'dark' ? '#000000' : '#ffffff');
  const speed = searchParams.get('speed') || 'medium';
  const height = searchParams.get('height') || '40px';
  const textColor = searchParams.get('color') || '#00ff41';
  const showIcons = searchParams.get('icons') !== 'false';
  const maxEvents = parseInt(searchParams.get('max')) || 10;

  const [events, setEvents] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  const tickerRef = useRef(null);
  const socketRef = useRef(null);

  const speedConfig = {
    slow: 25,
    medium: 40,
    fast: 60
  };

  // Event type icons
  const eventIcons = {
    'bull_run': '🚀',
    'market_crash': '💥',
    'volatility': '📊',
    'MARKET_OPEN': '🔔',
    'MARKET_CLOSE': '🔕',
    'stock_split': '✂️',
    'merger': '🤝',
    'earnings': '💰',
    'custom': '📢',
    'default': '📰'
  };

  const refreshInterval = parseInt(searchParams.get('refresh')) || 10000;

  const fetchEvents = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/events?limit=${maxEvents}`);
      if (response.ok) {
        const data = await response.json();
        setEvents(data.slice(0, maxEvents));
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  // Fetch initial events + socket + polling fallback
  useEffect(() => {
    fetchEvents();

    socketRef.current = io(API_BASE_URL, {
      auth: { public: true }
    });

    socketRef.current.on('market-event', (event) => {
      setEvents(prevEvents => {
        const newEvents = [event, ...prevEvents].slice(0, maxEvents);
        return newEvents;
      });
    });

    // Polling fallback
    const interval = setInterval(fetchEvents, refreshInterval);

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      clearInterval(interval);
    };
  }, [maxEvents, refreshInterval]);

  // Humanize event type: "FDA_REJECTION" -> "FDA Rejection"
  const humanizeType = (type) => {
    if (!type) return 'Market Event';
    return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace(/\bFda\b/i, 'FDA').replace(/\bCeo\b/i, 'CEO').replace(/\bCfo\b/i, 'CFO');
  };

  const formatEventText = (event) => {
    const icon = showIcons ? (eventIcons[event.type] || eventIcons.default) : '';
    const displayName = event.description || humanizeType(event.type);
    let text = '';

    if (event.changePercent !== undefined && event.changePercent !== null) {
      const sign = event.changePercent > 0 ? '+' : '';
      text = `${sign}${event.changePercent}% - ${displayName}`;
    } else if (event.multiplier && event.multiplier !== 1) {
      const percent = ((event.multiplier - 1) * 100).toFixed(1);
      const sign = percent > 0 ? '+' : '';
      text = `${sign}${percent}% - ${displayName}`;
    } else {
      text = displayName;
    }

    const time = new Date(event.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    return `${icon} ${time} - ${text}`;
  };

  const getAnimationDuration = () => {
    if (!tickerRef.current) return '90s';
    const contentWidth = tickerRef.current.scrollWidth;
    const pixelsPerSecond = speedConfig[speed] || speedConfig.medium;
    const duration = (contentWidth + window.innerWidth) / pixelsPerSecond;
    return `${duration}s`;
  };

  const animationDuration = getAnimationDuration();

  return (
    <div
      className={`embed-events-ticker ${theme}`}
      style={{
        background: bg === 'transparent' ? 'transparent' : bg,
        height,
      }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <button className="embed-fullscreen-btn embed-fullscreen-corner" onClick={() => document.documentElement.requestFullscreen?.()}>&#x26F6;</button>
      <div className="events-ticker-label">
        <span>📢 LIVE</span>
      </div>

      <div className="events-ticker-track">
        <div
          ref={tickerRef}
          className={`events-ticker-content ${isPaused ? 'paused' : ''}`}
          style={{
            animation: isPaused ? 'none' : `scroll-left ${animationDuration} linear infinite`,
          }}
        >
          {/* Double content for seamless loop */}
          {[0, 1].map((loopIndex) => (
            <div key={loopIndex} className="events-ticker-loop">
              {events.length > 0 ? (
                events.map((event, index) => {
                  const isNegative = event.changePercent < 0 || (event.multiplier && event.multiplier < 1);
                  return (
                    <span
                      key={`${loopIndex}-${index}`}
                      className={`events-ticker-item ${isNegative ? 'negative' : 'positive'}`}
                      style={{ color: isNegative ? '#ff4444' : textColor }}
                    >
                      {formatEventText(event)}
                      <span className="events-ticker-separator">•</span>
                    </span>
                  );
                })
              ) : (
                <span className="events-ticker-item" style={{ color: textColor }}>
                  Waiting for market events...
                  <span className="events-ticker-separator">•</span>
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EmbedEventsTicker;
